/**
 * agent.config.voices → 아바타 프리셋의 언어별 voice id 오버라이드
 * @layer entities/realtime-agent
 *
 * voice id 는 voice-server 카탈로그의 id 문자열 (예: "voxcpm2:hjh:ko").
 * 종료된 자체 TTS는 읽을 때 HJH로 전환한다. 저장된 설정과 외부 음성은 유지한다.
 */
export type AgentVoices = Partial<Record<'ko' | 'en' | 'ja' | 'zh', string>>;

export interface VoiceIdFields {
  korean_voice_id: string;
  english_voice_id: string;
  chinese_voice_id: string;
  japanese_voice_id: string;
}

const pick = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;

export function isRetiredInternalVoice(value: string): boolean {
  return /^(?:melo|cosyvoice3|qwen|qwen3tts|qwen3-tts|moss|moss-tts):/i.test(value.trim())
    || /^(?:NALNANI|COSYVOICE3)_/i.test(value.trim());
}

export function resolveVoiceId(value: string, language: keyof AgentVoices): string {
  return isRetiredInternalVoice(value) ? `voxcpm2:hjh:${language}` : value;
}

/** UI에서도 저장된 종료 모델을 현재 HJH 이름으로 표시한다. DB 쓰기는 하지 않는다. */
export function resolveAgentVoices(voices?: AgentVoices | null): AgentVoices {
  const result: AgentVoices = {};
  for (const lang of ['ko', 'en', 'ja', 'zh'] as const) {
    const value = pick(voices?.[lang]);
    if (value) result[lang] = resolveVoiceId(value, lang);
  }
  return result;
}

export function applyVoiceOverrides<T extends VoiceIdFields>(preset: T, voices?: AgentVoices | null): T {
  return {
    ...preset,
    korean_voice_id: resolveVoiceId(pick(voices?.ko) ?? preset.korean_voice_id, 'ko'),
    english_voice_id: resolveVoiceId(pick(voices?.en) ?? preset.english_voice_id, 'en'),
    chinese_voice_id: resolveVoiceId(pick(voices?.zh) ?? preset.chinese_voice_id, 'zh'),
    japanese_voice_id: resolveVoiceId(pick(voices?.ja) ?? preset.japanese_voice_id, 'ja'),
  };
}
